/**
 * Holds a live market detail page in its trading layout while a resolution
 * lands, so the page changes once instead of in pieces.
 *
 * On chain `resolve_drain_and_migrate` resolves and graduates a market in one
 * transaction, but the client learns about it in steps: `resolution.v0` sets
 * status `resolved`, `lifecycle.v0` sets `graduated`, and only a REST refetch
 * carries the winner's AMM pool (`outcomes[].graduated`), the prize total and
 * the resolution stats; the winner token's price comes from the winners row.
 * Rendering each step showed the legacy "graduating" UI, $0.00 stats and a
 * stats count-up before the final page.
 *
 * - `watching` — this page saw the market trading; it will hold when it resolves.
 * - `holding` — resolved or graduated, but the settled page's data is not in yet.
 * - `released` — the settled page renders (also any page first seen post-resolution).
 */
export type ResolutionHold = 'watching' | 'holding' | 'released';

export interface ResolutionHoldMarket {
  readonly status: string;
  readonly winnerOutcomeIndex?: number | null | undefined;
  readonly claimTotalClaimable?: string | null | undefined;
  readonly outcomes: ReadonlyArray<{
    readonly outcomeIndex: number;
    readonly graduated?: boolean | undefined;
  }>;
}

export function isPostResolutionStatus(status: string): boolean {
  return status === 'resolved' || status === 'graduated';
}

/** The hold for a page whose first market row has just loaded. */
export function initialResolutionHold(status: string): ResolutionHold {
  return isPostResolutionStatus(status) ? 'released' : 'watching';
}

export function hasIndexedPrizePoolTotal(value: string | null | undefined): boolean {
  return value !== null && value !== undefined && /^(?:0|[1-9]\d*)$/.test(value);
}

/**
 * The settled page has what it renders: the market is graduated, the refetched
 * row shows the winner's pool, and the winners row (token price) has loaded.
 */
export function isSettledPageReady(
  market: ResolutionHoldMarket,
  winnerRowLoaded: boolean,
): boolean {
  if (
    market.status !== 'graduated' ||
    !winnerRowLoaded ||
    !hasIndexedPrizePoolTotal(market.claimTotalClaimable)
  ) {
    return false;
  }
  const winner = market.outcomes.find(
    (outcome) => Number(outcome.outcomeIndex) === market.winnerOutcomeIndex,
  );
  return winner?.graduated === true;
}

/**
 * Advances the hold. Within the hold a stale REST row that still says
 * `trading` never drops the page back into the trading presentation. When the
 * hold times out the page follows the row: a settled row releases, a row that
 * still says `trading` goes back to watching so the real resolution can hold
 * again instead of painting in pieces.
 */
export function nextResolutionHold(
  hold: ResolutionHold,
  market: ResolutionHoldMarket,
  settledPageReady: boolean,
  timedOut: boolean,
): ResolutionHold {
  const post = isPostResolutionStatus(market.status);
  if (hold === 'watching' && post) return settledPageReady ? 'released' : 'holding';
  if (hold !== 'holding') return hold;
  if (settledPageReady) return 'released';
  if (timedOut) return post ? 'released' : 'watching';
  return hold;
}
